import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatExpansionPanel, MatExpansionPanelDescription, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatActionList, MatList, MatListItem } from '@angular/material/list';

import { NgSelectModule } from '@ng-select/ng-select';

import { BaasI18n } from '@lvflanders/bar';
import { BaasRemark, BaasRemarkList, BarI18nPipe } from '@lvflanders/bar-ng-ui';

import { DocumentModel } from '../../types/dido-document-model.type';
import { DocumenttypeModel } from '../../types/documenttype-model.type';

/** Dialog for the documenten control to modify or set the metadata. */
@Component({
    selector: 'dido-documenten-control-dialog',
    templateUrl: './dido-documenten-control-dialog.html',
    styleUrls: ['./dido-documenten-control-dialog.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatDialogTitle,
        MatDialogContent,
        BaasRemarkList,
        BaasRemark,
        MatExpansionPanel,
        MatExpansionPanelHeader,
        MatExpansionPanelTitle,
        MatExpansionPanelDescription,
        MatActionList,
        MatListItem,
        NgClass,
        MatList,
        ReactiveFormsModule,
        FormsModule,
        MatCheckbox,
        NgSelectModule,
        MatFormField,
        MatInput,
        MatDialogActions,
        MatButton,
        MatDialogClose,
        BarI18nPipe
    ]
})
export class DidoDocumentenControlDialog {
    // data given to current dialog
    readonly #data = inject(MAT_DIALOG_DATA);
    readonly #i18n = inject(BaasI18n);
    // reference to current dialog
    readonly #dialogRef = inject(MatDialogRef<DidoDocumentenControlDialog>);

    // the documenten to add or edit
    protected readonly documenten: DocumentModel[];
    // the document types for the dropdown
    protected readonly documenttypes: DocumenttypeModel[];
    // the title bovenaan the dialog
    protected readonly title: string;
    // whether the ok button has been clicked
    protected readonly confirmed = signal(false);
    // the currently selected document
    protected readonly selectedDocument = signal<DocumentModel>(undefined as unknown as DocumentModel);
    //if the checkbox is selected
    protected readonly applyAllSelected = signal<boolean>(false);
    protected readonly documentType = signal<DocumenttypeModel>(undefined as unknown as DocumenttypeModel);
    protected readonly onderwerp = signal<string>('');
    // Whether all validations are met (including from other documenten)
    protected readonly allValidationsNotMet = signal<boolean>(false);
    // whether in update or insert modus
    protected readonly update = signal<boolean>(false);
    /** Whether the documenttype on the current selected document is defined */
    protected readonly documenttypeNotSet = computed(() => !this.documentType()?.documenttypeCode || this.documentType().documenttypeCode.length == 0);

    constructor() {
        // assign the data
        this.documenten = this.#data.documenten;
        this.documenttypes = this.#data.documenttypes;
        this.documenttypes.sort((d1, d2) => {
            if (d1.omschrijving.trim().toLowerCase() < d2.omschrijving.trim().toLowerCase()) {
                return -1;
            }
            if (d1.omschrijving.trim().toLowerCase() > d2.omschrijving.trim().toLowerCase()) {
                return 1;
            }
            return 0;
        });
        this.update.set(this.#data.update); // set title based on update or insert and on the number of documenten
        this.title = this.#i18n.get(
            this.update()
                ? 'DOMAIN_DIDO.DOCUMENTEN_CONTROL.UPDATE_DOCUMENT_EXTERN'
                : this.documenten.length > 1
                  ? 'DOMAIN_DIDO.DOCUMENTEN_CONTROL.ADD_DOCUMENTEN_EXTERN'
                  : 'DOMAIN_DIDO.DOCUMENTEN_CONTROL.ADD_DOCUMENT_EXTERN'
        );

        this.#assignSelectedDocument(this.documenten[0]);
    }

    protected searchDocumenttypes(term: string, item: DocumenttypeModel) {
        term = term.toLowerCase();
        const termReplaced = term.replace(' ', '-');
        return item.omschrijving.toLowerCase().indexOf(term) > -1 || item.omschrijving.toLowerCase().indexOf(termReplaced) > -1;
    }

    protected onChange(change) {
        this.#assignSelectedDocument(change);
    }

    /** Occurs when the ok button is clicked. */
    protected confirm(): void {
        if (this.applyAllSelected()) {
            this.documenten.forEach((document) => {
                document.documenttypeCode = this.documentType()?.documenttypeCode;
                document.onderwerp = this.onderwerp();
                document.koppelenHuidigDossier = true;
            });
        } else {
            this.selectedDocument().onderwerp = this.onderwerp();
            this.selectedDocument().documenttypeCode = this.documentType()?.documenttypeCode;
            this.selectedDocument().documenttypeOmschrijving = this.documentType()?.omschrijving;

            this.documenten.forEach((document) => {
                document.koppelenHuidigDossier = true;
            });
        }

        this.confirmed.set(true);
        const invalid = this.#checkInvalid();
        this.allValidationsNotMet.set(invalid);
        if (!invalid) {
            this.#dialogRef.close(this.documenten);
        }
    }

    /**
     * Checks whether state is invalid.
     * @param documentNotToCheck
     */
    #checkInvalid(documentNotToCheck: DocumentModel | null = null): boolean {
        return this.documenten.some(
            (document) =>
                (documentNotToCheck == null || document != documentNotToCheck) && (document.documenttypeCode == null || document.documenttypeCode.length == 0)
        );
    }

    #assignSelectedDocument(document: DocumentModel) {
        this.selectedDocument.set(document);
        const documenttype = this.selectedDocument().documenttypeCode
            ? this.documenttypes.find((type) => type.documenttypeCode == this.selectedDocument().documenttypeCode)
            : undefined;
        this.documentType.set(documenttype as unknown as DocumenttypeModel);
    }
}
